import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { StorageFirestoreService } from '../../services/storage.firestore.service';
import { StorageStateModel } from './storage.store';
import { StorageActions } from './storage.action';
import { patchState } from '../../nested-value-getter';
import { batch } from 'typesaurus';
import { db } from '../../schema/orm.service';

/** Firestore's hard ceiling on writes in a single batch. */
const FIRESTORE_BATCH_LIMIT = 500;

@State<StorageStateModel>({
  name: 'storages',
  defaults: {
    storages: [],
    isSubscribed: false,
    isLoading: false,
    filter: undefined,
    sort: [],
  },
})
@Injectable()
export class StorageState {
  private storageService = inject(StorageFirestoreService);

  @Action(StorageActions.GetAllStorage)
  async getAllStorages(ctx: StateContext<StorageStateModel>) {
    ctx.patchState({ isLoading: true });
    const storages = await this.storageService.GetAll();
    ctx.patchState({ storages: storages.map(({ data, ref }) => ({ ...data, ref })), isLoading: false });
  }

  @Action(StorageActions.InitStorages)
  initMachines(ctx: StateContext<StorageStateModel>) {
    const state = ctx.getState();
    if (!state.isSubscribed) {
      ctx.patchState({ isSubscribed: true });
      const off = this.storageService.OnGetAll((data) => ctx.dispatch(new StorageActions.UpdateStorageResult(data)));
      off.catch((err) => ctx.dispatch(new StorageActions.StorageError(err as Error)));
      ctx.patchState({ off });
    }
  }

  @Action(StorageActions.CloseStoragesConnection)
  closeMachinesConnection(ctx: StateContext<StorageStateModel>) {
    const state = ctx.getState();
    if (state.isSubscribed && state.off) {
      state.off();
    }
    ctx.patchState({ isSubscribed: false });
  }

  @Action(StorageActions.UpdateStorage)
  async updateMachine(ctx: StateContext<StorageStateModel>, { storageData, ref }: StorageActions.UpdateStorage) {
    await this.storageService.Update(ref, storageData);
  }

  @Action(StorageActions.UpdateStorageResult)
  updateStorageResult(ctx: StateContext<StorageStateModel>, { data }: StorageActions.UpdateStorageResult) {
    patchState<StorageStateModel>(ctx, 'storages', data);
  }

  @Action(StorageActions.PaginateQueryStorage)
  async paginateQueryStorage(
    ctx: StateContext<StorageStateModel>,
    { request, isResetAfterFilterOrSort }: StorageActions.PaginateQueryStorage,
  ) {
    const state = ctx.getState();
    ctx.patchState({ isLoading: true });
    const { runs, direction } = this.storageService.SetQuery({
      ...request,
      count: state.storages.length,
      data: state.storages,
    });
    if (isResetAfterFilterOrSort) {
      // const array: StorageStoreModel[] = [];
      // array.length = state.storages.length;
      // ctx.patchState({ storages: array });

      const trCount = state.storages.length;
      state.storages = [];
      state.storages.length = trCount;
    }
    if (JSON.stringify(state.filter) !== JSON.stringify(request.filterModel)) {
      ctx.patchState({ filter: request?.filterModel });

      // const array: StorageStoreModel[] = [];
      // array.length = await this.storageService.GetBuildedCount(runs);
      // ctx.patchState({ storages: array });

      state.storages = [];
      state.storages.length = await this.storageService.GetBuildedCount(runs);
    }
    if (state.storages.length !== 0) {
      runs.push(...this.storageService.SetLimit({ ...request, count: state.storages.length, data: [] }));
      ctx.patchState({ sort: request?.sortModel });

      if (!runs.some((x) => x.type === 'limit')) {
        throw new Error('No limit');
      }
      const storages = await this.storageService.GetBuildedData(runs);
      ctx.patchState({ filter: request?.filterModel });

      if (direction === 'asc') {
        storages.reverse().forEach(
          ({ data, ref }, i) =>
            (state.storages[i + (request?.startRow ?? 0)] = {
              ...data,
              ref,
            }),
        );
      } else {
        storages.forEach(
          ({ data, ref }, i) =>
            (state.storages[i + (request?.startRow ?? 0)] = {
              ...data,
              ref,
            }),
        );
      }
    }

    ctx.patchState({ isLoading: false, storages: state.storages });
  }

  @Action(StorageActions.QueryStorage)
  async queryStorage(ctx: StateContext<StorageStateModel>, { filter }: StorageActions.QueryStorage) {
    ctx.patchState({ isLoading: true });
    const storages = await this.storageService.QueryAll(filter.query);
    ctx.patchState({ storages: storages.map(({ data, ref }) => ({ ...data, ref })), isLoading: false });
  }

  /**
   * One batch, so a bulk create either lands whole or not at all.
   *
   * This was `Promise.all` over independent `add` calls. Firestore has no rollback across those:
   * if the fourth write failed, the first three stayed, `patchState` was never reached so the grid
   * never showed them, and the obvious response, submitting the dialog again, minted fresh cIds
   * and duplicated the three that had already succeeded. Ids are minted up front because a batch
   * has no `add`, which is also what makes the write idempotent: committing the same batch twice
   * overwrites the same documents instead of adding more.
   */
  @Action(StorageActions.AddStorageItem)
  async addStorageItem(ctx: StateContext<StorageStateModel>, { items }: StorageActions.AddStorageItem) {
    if (items.length === 0) {
      return;
    }
    if (items.length > FIRESTORE_BATCH_LIMIT) {
      // Splitting would silently give up the atomicity this exists for, so refuse instead. The
      // dialog cannot currently produce a batch anywhere near this size.
      throw new Error(
        `Cannot create ${items.length} storage items atomically: a Firestore batch holds at most ${FIRESTORE_BATCH_LIMIT} writes.`,
      );
    }

    const storages = ctx.getState().storages;
    const ids = await Promise.all(items.map(() => db.storage.id()));

    const storageBatch = batch(db);
    items.forEach((item, index) =>
      storageBatch.storage.set(
        ids[index],
        // `createdAt` is optional on BaseSchema, and typesaurus only offers `remove()` for an
        // optional field, so the server-date sentinel has to be asserted past its write type.
        // `AbstractGenFirestoreService.Create` makes the same assertion for the same reason; this
        // one is narrowed to the single value rather than the whole document. It stays a server
        // date on purpose: the alternative is ordering the storage list by the client's clock.
        ($) => ({ ...item, createdAt: $.serverDate() as unknown as Date }),
      ),
    );
    await storageBatch();

    patchState<StorageStateModel>(ctx, 'storages', [
      ...items.map((item, index) => ({ ...item, ref: db.storage.ref(ids[index]) })),
      ...storages,
    ]);
  }

  @Action(StorageActions.StorageError)
  machineError(ctx: StateContext<StorageStateModel>, { error }: StorageActions.StorageError) {
    console.error({ error });
  }

  @Selector()
  static storages(state: StorageStateModel) {
    return state.storages;
  }

  @Selector()
  static storageState(state: StorageStateModel) {
    return state;
  }
}
